define((require, exports, module) => {
    'use strict';

    const UploadReason = require('../../UploadReason');
    const JavaScriptError = require('../../errors/JavaScriptError');
    const LocalFileNotFoundFail = require('../../fails/LocalFileNotFoundFail');
    const UnsupportedFolderTransferFail = require('../../fails/UnsupportedFolderTransferFail');

    const MB = 1024 * 1024;

    /**
     * @namespace
     */
    const fileSystemFileReader = {
        /**
         * На самом деле значение гораздо больше,
         * но нас интересуют файлы меньше 21 байт.
         * @type {number}
         */
        MAX_BLOB_SIZE_PER_READ: 21,

        /**
         * В Safari возникает ошибка при попытке прочитать файл более 2GB.
         * Firefox выбрасывает исключение NS_ERROR_OUT_OF_MEMORY,
         * когда размер файла превышает объем свободной памяти.
         * В Chrome может падать вкладка, когда не хватает памяти.
         * Поэтому будем пытаться читать файлы только небольшого размера.
         * @type {number}
         */
        MAX_READABLE_SIZE: 30 * MB,

        /**
         * @returns {Promise}
         * @resolves {FileReader}
         * @rejects {Error}
         */
        createReader() {
            return new Promise((resolve) => {
                const reader = new FileReader();

                resolve(reader);
            });
        },

        /**
         * @param {FileReader} reader
         * @param {File} file
         * @param {boolean} [whole]
         * @returns {Promise}
         * @resolves {ArrayBuffer}
         * @rejects {UploadReason|Error}
         */
        // eslint-disable-next-line
        _readFile(reader, file, whole) {
            let promise = new Promise((resolve) => {
                reader.addEventListener('error', resolve);
                reader.addEventListener('load', resolve);
                reader.addEventListener('loadend', resolve);

                if (!whole) {
                    reader.addEventListener('progress', resolve);
                }

                reader.readAsArrayBuffer(file);
            });

            promise = promise.then((event) => {
                // TODO: removeEventListener?
                let promise;

                if (event.type === 'error') {
                    const source = UploadReason.SOURCE_WEB_CLIENT;
                    let stack;
                    let reason;
                    const error = reader.error;

                    if (LocalFileNotFoundFail.isNotFoundError(error)) {
                        stack = new Error('LocalFileNotFoundFail');
                        reason = new LocalFileNotFoundFail(stack, source);
                    } else if (
                        UnsupportedFolderTransferFail.isNotReadableError(error)
                    ) {
                        stack = new Error('UnsupportedFolderTransferFail');
                        reason = new UnsupportedFolderTransferFail(
                            stack,
                            source,
                        );
                    } else {
                        stack = new Error('JavaScriptError');
                        reason = new JavaScriptError(stack, source, error);
                    }

                    promise = Promise.reject(reason);
                } else {
                    if (reader.readyState === reader.LOADING) {
                        reader.abort();
                    }

                    promise = Promise.resolve(reader.result);
                }

                return promise;
            });

            if (!whole) {
                // Если файл не считывается целиком, значит чтение используется
                // для проверки существования файла, поэтому чтобы не блокировать
                // возможность аплоада, игнорируем ошибку "Out of Memory".
                promise = promise.catch((reason) => {
                    let promise;

                    if (reason && reason.name === 'NS_ERROR_OUT_OF_MEMORY') {
                        promise = Promise.resolve(null);
                    } else {
                        promise = Promise.reject(reason);
                    }

                    return promise;
                });
            }

            return promise;
        },

        /**
         * @param {File} file
         * @returns {Promise}
         * @resolves {File}
         * @rejects {UploadReason|Error}
         */
        readFile(file) {
            let promise;

            if (file.size > fileSystemFileReader.MAX_READABLE_SIZE) {
                promise = Promise.reject(new Error('too large'));
            } else {
                promise = fileSystemFileReader.createReader(file);

                promise = promise.then((reader) => {
                    return fileSystemFileReader._readFile(reader, file);
                });

                promise = promise.then(() => {
                    return file;
                });
            }

            return promise;
        },
    };

    module.exports = fileSystemFileReader;
});
